import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { UserCircle, Radio, Loader2, MapPin, ArrowLeft } from "lucide-react";
import api, { API } from "@/lib/api";

export default function HostProfile() {
  const { id } = useParams();
  const [host, setHost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/hosts/${id}`).then((r) => setHost(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#E6B34A]" /></div>;
  if (notFound || !host) return <div className="px-4 sm:px-6 lg:px-12 py-20 text-center text-zinc-400" data-testid="host-notfound">Host not found.</div>;

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-10 max-w-3xl">
      <Link to="/live" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-[#E6B34A] mb-6"><ArrowLeft className="w-4 h-4" /> Back to the show</Link>
      <div className="flex items-center gap-5" data-testid="host-profile">
        <div className="w-24 h-24 rounded-full overflow-hidden bg-[#141C42] border border-white/10 flex items-center justify-center shrink-0">
          {host.has_avatar ? <img src={`${API}/users/${host.id}/avatar`} alt={host.name} className="w-full h-full object-cover" /> : <UserCircle className="w-12 h-12 text-zinc-600" />}
        </div>
        <div>
          {host.title && <p className="overline text-[#E6B34A]">{host.title}</p>}
          <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">{host.name}</h1>
          {(host.city || host.state) && <p className="text-sm text-zinc-400 mt-1 flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {[host.city, host.state].filter(Boolean).join(", ")}</p>}
          <p className="text-sm text-zinc-500 mt-1">{host.episodes.length} episode{host.episodes.length === 1 ? "" : "s"}</p>
        </div>
      </div>

      <h2 className="font-display font-extrabold text-xl tracking-tight mt-10 mb-5 flex items-center gap-2"><Radio className="w-5 h-5 text-[#E6B34A]" /> Episodes</h2>
      <div className="space-y-4" data-testid="host-episodes-list">
        {host.episodes.length === 0 ? <p className="text-zinc-500">No episodes yet.</p> :
          host.episodes.map((ep) => (
            <div key={ep.id} className="rounded-2xl border border-white/5 bg-[#141C42] p-5 flex gap-4">
              <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#0C1338] border border-white/10 flex items-center justify-center shrink-0">
                {ep.has_cover ? <img src={`${API}${ep.cover_url}`} alt="" className="w-full h-full object-cover" /> : <Radio className="w-5 h-5 text-zinc-600" />}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-display font-bold text-lg tracking-tight truncate">{ep.title}</h3>
                {ep.description && <p className="text-sm text-zinc-400 mt-0.5 line-clamp-2">{ep.description}</p>}
                <audio controls className="w-full mt-2 h-9"><source src={`${API}/episodes/${ep.id}/audio`} /></audio>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
